<template>
  <div class="student-management">
    <div class="page-header">
      <h2>学员管理</h2>
      <el-button type="primary" icon="el-icon-plus" @click="handleAddStudent">
        添加学员
      </el-button>
    </div>

    <!-- 搜索和筛选 -->
    <el-card class="search-card" shadow="never">
      <el-form :inline="true" :model="searchForm" class="search-form">
        <el-form-item label="学员姓名">
          <el-input
            v-model="searchForm.real_name"
            placeholder="请输入学员姓名"
            clearable
            @clear="handleSearch"
          />
        </el-form-item>
        <el-form-item label="手机号">
          <el-input
            v-model="searchForm.mobile"
            placeholder="请输入手机号"
            clearable
            @clear="handleSearch"
          />
        </el-form-item>
        <el-form-item label="状态">
          <el-select
            v-model="searchForm.status"
            placeholder="请选择状态"
            clearable
            @clear="handleSearch"
          >
            <el-option label="全部" value="" />
            <el-option label="在学" value="active" />
            <el-option label="停课" value="inactive" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" icon="el-icon-search" @click="handleSearch">
            搜索
          </el-button>
          <el-button icon="el-icon-refresh" @click="handleReset">
            重置
          </el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 学员列表 -->
    <el-card class="table-card" shadow="never">
      <el-table
        v-loading="loading"
        :data="studentList"
        style="width: 100%"
        :header-cell-style="{ background: '#fafafa', color: '#333' }"
      >
        <el-table-column type="index" label="序号" width="60" />
        <el-table-column prop="real_name" label="学员姓名" min-width="100" />
        <el-table-column prop="mobile" label="手机号" min-width="120" />
        <el-table-column prop="grade" label="年级" min-width="80" />
        <el-table-column label="累计学习单词" min-width="120">
          <template slot-scope="scope">
            <span class="highlight-number">{{ scope.row.stats?.totalWords || 0 }}</span>
          </template>
        </el-table-column>
        <el-table-column label="学习天数" min-width="100">
          <template slot-scope="scope">
            <span>{{ scope.row.stats?.studyDays || 0 }} 天</span>
          </template>
        </el-table-column>
        <el-table-column prop="status" label="状态" min-width="80">
          <template slot-scope="scope">
            <el-tag :type="getStatusType(scope.row.status)" size="small">
              {{ getStatusText(scope.row.status) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="createtime" label="添加时间" min-width="160">
          <template slot-scope="scope">
            {{ formatTimestamp(scope.row.createtime) }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="240" fixed="right">
          <template slot-scope="scope">
            <el-button
              type="text"
              icon="el-icon-view"
              size="small"
              @click="handleViewDetail(scope.row)"
            >
              详情
            </el-button>
            <el-button
              type="text"
              icon="el-icon-edit"
              size="small"
              @click="handleEdit(scope.row)"
            >
              编辑
            </el-button>
            <!-- <el-button
              type="text"
              icon="el-icon-setting"
              size="small"
              @click="handleSettings(scope.row)"
            >
              设置
            </el-button> -->
            <el-button
              type="text"
              icon="el-icon-delete"
              size="small"
              style="color: #f56c6c"
              @click="handleDelete(scope.row)"
            >
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination-container">
        <el-pagination
          :current-page="pagination.currentPage"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="pagination.pageSize"
          :total="pagination.total"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </el-card>

    <!-- 添加/编辑学员对话框 -->
    <student-form-dialog
      :visible.sync="showFormDialog"
      :student-data="currentStudent"
      :is-edit="isEdit"
      @success="handleFormSuccess"
    />

    <!-- 学员设置对话框 -->
    <student-settings-dialog
      :visible.sync="showSettingsDialog"
      :student-data="currentStudent"
      @success="handleSettingsSuccess"
    />
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import StudentFormDialog from '@/components/teacher/StudentFormDialog.vue';
import StudentSettingsDialog from '@/components/teacher/StudentSettingsDialog.vue';
import { formatTimestamp } from '@/utils/time';

export default {
  name: 'StudentManagement',
  components: {
    StudentFormDialog,
    StudentSettingsDialog,
  },
  data() {
    return {
      loading: false,
      searchForm: {
        real_name: '',
        mobile: '',
        status: '',
      },
      pagination: {
        currentPage: 1,
        pageSize: 10,
        total: 0,
      },
      showFormDialog: false,
      showSettingsDialog: false,
      currentStudent: null,
      isEdit: false,
    };
  },
  computed: {
    ...mapState('student', ['studentList']),
  },
  created() {
    this.loadStudentList();
  },
  methods: {
    ...mapActions('student', ['fetchStudentList', 'deleteStudent']),

    // 时间格式化方法
    formatTimestamp,

    /**
     * 获取状态类型
     */
    getStatusType(status) {
      const typeMap = {
        normal: 'success',
        inactive: 'info',
      };
      return typeMap[status] || 'info';
    },

    /**
     * 获取状态文本
     */
    getStatusText(status) {
      const textMap = {
        normal: '在学',
        inactive: '停课',
      };
      return textMap[status] || '未知';
    },

    /**
     * 加载学员列表
     */
    async loadStudentList() {
      console.log("加载学员列表...");
      this.loading = true;
      try {
        const params = {
          ...this.searchForm,
          page: this.pagination.currentPage,
          pageSize: this.pagination.pageSize,
        };
        const result = await this.fetchStudentList(params);
        console.log("学员列表结果:", result);
        this.pagination.total = result.total || 0;
      } catch (error) {
        console.error('加载学员列表失败:', error);
        this.$message.error('加载学员列表失败');
      } finally {
        this.loading = false;
      }
    },

    /**
     * 搜索
     */
    handleSearch() {
      this.pagination.currentPage = 1;
      this.loadStudentList();
    },

    /**
     * 重置搜索
     */
    handleReset() {
      this.searchForm = {
        name: '',
        phone: '',
        status: '',
      };
      this.handleSearch();
    },

    /**
     * 添加学员
     */
    handleAddStudent() {
      this.currentStudent = null;
      this.isEdit = false;
      this.showFormDialog = true;
    },

    /**
     * 编辑学员
     */
    handleEdit(student) {
      this.currentStudent = { ...student };
      this.isEdit = true;
      this.showFormDialog = true;
    },

    /**
     * 查看详情
     */
    handleViewDetail(student) {
      this.$router.push({
        name: 'StudentDetail',
        params: { id: student.id },
      });
    },

    /**
     * 学员设置
     */
    handleSettings(student) {
      this.currentStudent = { ...student };
      this.showSettingsDialog = true;
    },

    /**
     * 删除学员
     */
    handleDelete(student) {
      this.$confirm(
        `确定要删除学员"${student.name}"吗？删除后将无法恢复！`,
        '警告',
        {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning',
        }
      ).then(async () => {
        try {
          await this.deleteStudent(student.id);
          this.$message.success('删除成功');
          this.loadStudentList();
        } catch (error) {
          console.error('删除学员失败:', error);
          this.$message.error('删除学员失败');
        }
      });
    },

    /**
     * 表单提交成功
     */
    handleFormSuccess() {
      this.showFormDialog = false;
      this.loadStudentList();
    },

    /**
     * 设置提交成功
     */
    handleSettingsSuccess() {
      this.showSettingsDialog = false;
      this.loadStudentList();
    },

    /**
     * 分页大小改变
     */
    handleSizeChange(val) {
      this.pagination.pageSize = val;
      this.pagination.currentPage = 1;
      this.loadStudentList();
    },

    /**
     * 当前页改变
     */
    handleCurrentChange(val) {
      this.pagination.currentPage = val;
      this.loadStudentList();
    },
  },
};
</script>

<style scoped>
.student-management {
  padding: 20px;
  max-width: 1400px;
  margin: 0 auto;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.page-header h2 {
  font-size: 24px;
  font-weight: 600;
  color: #303133;
  margin: 0;
}

.search-card {
  margin-bottom: 20px;
}

.search-form {
  margin-bottom: 0;
}

.table-card {
  min-height: 500px;
}

.highlight-number {
  color: #409eff;
  font-weight: 600;
}

.pagination-container {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .student-management {
    padding: 10px;
  }

  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }

  .search-form {
    display: block;
  }

  .search-form .el-form-item {
    display: block;
    margin-right: 0;
  }
}
</style>
